import React, { createContext, useState, useContext, useEffect } from "react";

// Create context
const ThemeContext = createContext();

// Available themes configuration
const AVAILABLE_THEMES = {
  light: { name: "Light", code: "light", icon: "fa-sun-o" },
  dark: { name: "Dark", code: "dark", icon: "fa-moon-o" }
};

// Default theme
const DEFAULT_THEME = "light";

// Storage key for persisting theme preference
const THEME_STORAGE_KEY = "fiq_theme";

// Theme detection and initialization utility
const detectInitialTheme = () => {
  try {
    // 1. Check localStorage for saved preference
    const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    if (savedTheme && AVAILABLE_THEMES[savedTheme]) {
      console.log('Using saved theme preference:', savedTheme);
      return savedTheme;
    }

    // 2. Check system theme preference
    // if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
    //   console.log('Using system dark theme preference');
    //   return 'dark';
    // }

    // 3. Fallback to default theme
    console.log('Using default theme:', DEFAULT_THEME);
    return DEFAULT_THEME;
  } catch (error) {
    console.error('Error detecting initial theme:', error);
    return DEFAULT_THEME;
  }
};

// Provider component
export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => detectInitialTheme());

  // Apply theme to document root
  useEffect(() => {
    const root = document.documentElement;
    
    // Remove any existing theme classes
    Object.keys(AVAILABLE_THEMES).forEach(theme => {
      root.classList.remove(`theme-${theme}`);
    });
    
    // Add current theme class
    root.classList.add(`theme-${currentTheme}`);
    
    // Set CSS custom property for theme
    root.style.setProperty('--current-theme', currentTheme);
    
    console.log('Theme applied to document:', currentTheme);
  }, [currentTheme]);

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleSystemThemeChange = (e) => {
      
      // Only auto-switch if user hasn't manually selected a theme
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      if (!savedTheme) {
        const newTheme = e.matches ? 'dark' : 'light';
        console.log('System theme changed to:', newTheme);
        setCurrentTheme(newTheme);
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, []);

  // Function to change theme and persist the choice
  const changeTheme = React.useCallback((themeCode) => {
    console.log('=== changeTheme called ===');
    console.log('Requested theme:', themeCode);
    console.log('Available themes:', Object.keys(AVAILABLE_THEMES));
    console.log('Current theme before change:', currentTheme);
    
    if (AVAILABLE_THEMES[themeCode]) {
      console.log('Changing theme to:', themeCode);
      setCurrentTheme(themeCode);
      
      try {
        localStorage.setItem(THEME_STORAGE_KEY, themeCode);
        console.log('Theme preference saved to localStorage:', themeCode);
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    } else {
      console.error('Unsupported theme code:', themeCode);
    }
    console.log('=== changeTheme completed ===');
  }, [currentTheme]);

  // Toggle between light and dark themes
  const toggleTheme = React.useCallback(() => {
    const newTheme = currentTheme === 'dark' ? 'light' : 'dark';
    changeTheme(newTheme);
  }, [currentTheme, changeTheme]);

  // Get theme info
  const currentThemeInfo = React.useMemo(() => {
    return AVAILABLE_THEMES[currentTheme] || AVAILABLE_THEMES[DEFAULT_THEME];
  }, [currentTheme]);

  // Context value
  const contextValue = React.useMemo(() => ({
    currentTheme,
    currentThemeInfo,
    availableThemes: AVAILABLE_THEMES,
    changeTheme,
    toggleTheme,
    isDark: currentTheme === 'dark',
    isLight: currentTheme === 'light'
  }), [currentTheme, currentThemeInfo, changeTheme, toggleTheme]);

  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Helper function to get available themes (can be imported anywhere)
export const getAvailableThemes = () => AVAILABLE_THEMES;

// Helper function to get current theme from storage (can be imported anywhere)
export const getCurrentThemeFromStorage = () => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) || DEFAULT_THEME;
  } catch (error) {
    console.error('Error reading theme from storage:', error);
    return DEFAULT_THEME;
  }
};

// Helper function to set theme in storage (can be imported anywhere)
export const setThemeInStorage = (themeCode) => {
  try {
    if (AVAILABLE_THEMES[themeCode]) {
      localStorage.setItem(THEME_STORAGE_KEY, themeCode);
      return true;
    }
    return false;
  } catch (error) {
    console.error('Error saving theme to storage:', error);
    return false;
  }
};
